import {
  assertMutationId,
  cloneWorkspaceDirectory,
  emptyWorkspaceDirectory,
  type WorkspaceCommitResult,
  type WorkspaceDirectory,
  type WorkspaceListener,
  type WorkspaceMutation,
  type WorkspaceRepository,
} from './WorkspaceRepository'

export interface IndexedDbWorkspaceAdapterOptions {
  databaseName?: string
  objectStoreName?: string
  channelName?: string
  indexedDb?: IDBFactory
  broadcastChannel?: BroadcastChannel
}

const RECORD_KEY = 'directory'
const INVALIDATION_KIND = 'openelp-workspace-invalidated'

function getDefaultIndexedDb(): IDBFactory {
  if (typeof indexedDB === 'undefined') throw new Error('IndexedDB is unavailable; workspace remains read-only')
  return indexedDB
}

function getError(event: Event, fallback: string): Error {
  const request = event.target as IDBRequest | null
  return request?.error ?? new Error(fallback)
}

export class IndexedDbWorkspaceAdapter implements WorkspaceRepository {
  private readonly databaseName: string
  private readonly objectStoreName: string
  private readonly indexedDb: IDBFactory
  private readonly listeners = new Set<WorkspaceListener>()
  private readonly channel?: BroadcastChannel
  private databasePromise: Promise<IDBDatabase> | null = null

  constructor(options: IndexedDbWorkspaceAdapterOptions = {}) {
    this.databaseName = options.databaseName ?? 'openelp-workspace-v1'
    this.objectStoreName = options.objectStoreName ?? 'workspace'
    this.indexedDb = options.indexedDb ?? getDefaultIndexedDb()
    this.channel = options.broadcastChannel ?? (
      typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(options.channelName ?? 'openelp-workspace')
    )
    if (this.channel) {
      this.channel.addEventListener('message', (event) => {
        if (event.data?.kind !== INVALIDATION_KIND) return
        void this.load().then((snapshot) => this.publish(snapshot))
      })
    }
  }

  async load(): Promise<WorkspaceDirectory> {
    const database = await this.openDatabase()
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(this.objectStoreName, 'readwrite')
      const store = transaction.objectStore(this.objectStoreName)
      const request = store.get(RECORD_KEY)
      let snapshot: WorkspaceDirectory | null = null
      request.onsuccess = () => {
        snapshot = request.result
          ? cloneWorkspaceDirectory(request.result as WorkspaceDirectory)
          : emptyWorkspaceDirectory()
        if (!request.result) store.put(snapshot, RECORD_KEY)
      }
      request.onerror = (event) => reject(getError(event, 'Unable to read workspace'))
      transaction.oncomplete = () => resolve(snapshot ?? emptyWorkspaceDirectory())
      transaction.onerror = (event) => reject(getError(event, 'Unable to read workspace'))
      transaction.onabort = () => reject(transaction.error ?? new Error('Workspace read transaction aborted'))
    })
  }

  async transact(expectedRevision: number, mutationId: string, mutation: WorkspaceMutation): Promise<WorkspaceCommitResult> {
    assertMutationId(mutationId)
    const database = await this.openDatabase()
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(this.objectStoreName, 'readwrite')
      const store = transaction.objectStore(this.objectStoreName)
      const request = store.get(RECORD_KEY)
      let result: WorkspaceCommitResult | null = null
      request.onsuccess = () => {
        const current = request.result
          ? cloneWorkspaceDirectory(request.result as WorkspaceDirectory)
          : emptyWorkspaceDirectory()
        const prior = current.mutationReceipts?.[mutationId]
        if (prior) {
          result = { status: 'duplicate', mutationId, revision: prior.revision, snapshot: cloneWorkspaceDirectory(prior.snapshot as WorkspaceDirectory) }
          return
        }
        if (current.revision !== expectedRevision) {
          result = { status: 'conflict', mutationId, expectedRevision, revision: current.revision, snapshot: current }
          return
        }
        const draft = cloneWorkspaceDirectory(current)
        try {
          mutation(draft)
        } catch (error) {
          transaction.abort()
          reject(error)
          return
        }
        const revision = current.revision + 1
        const next: WorkspaceDirectory = { ...draft, revision }
        const receiptSnapshot = cloneWorkspaceDirectory({ ...next, mutationReceipts: undefined })
        next.mutationReceipts = {
          ...(current.mutationReceipts ?? {}),
          [mutationId]: { revision, snapshot: receiptSnapshot },
        }
        store.put(next, RECORD_KEY)
        result = { status: 'committed', mutationId, revision, snapshot: cloneWorkspaceDirectory(next) }
      }
      request.onerror = (event) => reject(getError(event, 'Unable to read workspace'))
      transaction.oncomplete = () => {
        if (!result) {
          reject(new Error('Workspace transaction completed without a result'))
          return
        }
        if (result.status === 'committed') {
          this.publish(result.snapshot)
          this.channel?.postMessage({ kind: INVALIDATION_KIND, revision: result.revision, mutationId })
        }
        resolve(result)
      }
      transaction.onerror = (event) => reject(getError(event, 'Unable to write workspace'))
      transaction.onabort = () => {
        if (!result) reject(transaction.error ?? new Error('Workspace write transaction aborted'))
      }
    })
  }

  subscribe(listener: WorkspaceListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  async exportRecovery(): Promise<WorkspaceDirectory> {
    return this.load()
  }

  close(): void {
    this.channel?.close()
    if (this.databasePromise) void this.databasePromise.then((database) => database.close())
  }

  private publish(snapshot: WorkspaceDirectory): void {
    const published = cloneWorkspaceDirectory(snapshot)
    for (const listener of this.listeners) {
      try { listener(published) } catch { /* subscribers cannot undo a committed transaction */ }
    }
  }

  private openDatabase(): Promise<IDBDatabase> {
    if (this.databasePromise) return this.databasePromise
    this.databasePromise = new Promise((resolve, reject) => {
      const request = this.indexedDb.open(this.databaseName, 1)
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(this.objectStoreName)) request.result.createObjectStore(this.objectStoreName)
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = (event) => reject(getError(event, 'Unable to open workspace database'))
      request.onblocked = () => reject(new Error('Workspace database upgrade is blocked by another tab'))
    })
    return this.databasePromise
  }
}
